// src/components/superadmin/CompanyManagement.jsx

import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Country, State } from "country-state-city";
import { debounce } from "lodash";

// ── Service ───────────────────────────────────────────────────────────────────
import { companyService } from "../../services/companyService";

// ── Auth ──────────────────────────────────────────────────────────────────────
import { useAuth } from "../../contexts/AuthContext";

// ── MUI ───────────────────────────────────────────────────────────────────────
import {
  Box, Container, Typography, Button, Grid, Paper,
  TextField, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TablePagination, IconButton,
  Chip, InputAdornment, Tooltip, Menu, MenuItem,
  Divider, Fade, Avatar, Skeleton, Alert,
} from "@mui/material";

import AddIcon          from "@mui/icons-material/Add";
import SearchIcon       from "@mui/icons-material/Search";
import ClearIcon        from "@mui/icons-material/Clear";
import EditIcon         from "@mui/icons-material/Edit";
import DeleteIcon       from "@mui/icons-material/Delete";
import MoreVertIcon     from "@mui/icons-material/MoreVert";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import RefreshIcon      from "@mui/icons-material/Refresh";
import ToggleOnIcon     from "@mui/icons-material/ToggleOn";
import ToggleOffIcon    from "@mui/icons-material/ToggleOff";
import EmailIcon        from "@mui/icons-material/Email";
import PhoneIcon        from "@mui/icons-material/Phone";
import LanguageIcon     from "@mui/icons-material/Language";
import LocationOnIcon   from "@mui/icons-material/LocationOn";

// ── Modals ────────────────────────────────────────────────────────────────────
import CreateEditCompanyModal from "../../components/Modals/CreateEditCompanyModal";
import CompanyDeleteModal     from "../../components/Modals/CompanyDeleteModal";
import ToastNotification      from "../../components/common/ToastNotification";
import PermissionDenied       from "../../components/common/PermissionDenied";

/* ═══════════════════════════════════════════════════════════════════════════
   HELPERS
═══════════════════════════════════════════════════════════════════════════ */


const COUNTRY_BY_CODE = new Map(
  Country.getAllCountries().map((c) => [c.isoCode, c.name]),
);

const getCountryName = (code) => {
  if (!code) return "—";
  return COUNTRY_BY_CODE.get(code) || code;
};

const getStateName = (countryCode, stateCode) => {
  if (!countryCode || !stateCode) return "—";
  const s = State.getStateByCodeAndCountry(stateCode, countryCode);
  return s ? s.name : stateCode;
};

const convertTimestampToDate = (ts) => {
  if (!ts) return null;
  if (ts instanceof Date) return ts;
  if (ts?.toDate)         return ts.toDate();
  if (ts?.seconds)        return new Date(ts.seconds * 1000);
  if (typeof ts === "string") return new Date(ts);
  return null;
};

const normaliseCompany = (raw) => ({
  id:                   raw.id || raw.companyId || raw.docId || raw._id,
  companyId:            raw.companyId            || raw.id,
  name:                 raw.name                 || "—",
  contactPerson:        raw.contactPersonName     || raw.contactPerson || "—",
  email:                raw.email                || "—",
  phoneNumber:          raw.phoneNumber          || "—",
  website:              raw.website              || "—",
  gstin:                raw.gstin                || "—",
  address:              raw.address              || "—",
  address1:             raw.address1             || "—",
  address2:             raw.address2             || "—",
  address3:             raw.address3             || "—",
  country:
    raw.country ||
    (raw.countryCode ? getCountryName(raw.countryCode) : "—"),
  countryCode:          raw.countryCode          || "—",
  state:
    raw.state ||
    (raw.stateCode && raw.countryCode
      ? getStateName(raw.countryCode, raw.stateCode)
      : "—"),
  stateCode:            raw.stateCode            || "—",
  city:                 raw.city                 || "—",
  pincode:              raw.pincode              || "—",
  assets:               raw.assets               || 0,
  userCount:            raw.userCount            || 0,
  status:               raw.status               || "Active",
    createdAt:            convertTimestampToDate(raw.metadata?.createdAt ?? raw.createdAt),
  updatedAt:            convertTimestampToDate(raw.metadata?.updatedAt ?? raw.updatedAt),
  createdBy:            raw.metadata?.createdBy  || raw.createdBy  || "—",
  updatedBy:            raw.metadata?.updatedBy  || raw.updatedBy  || "—",
  localSupabaseURL:     raw.localSupabaseURL     || "",
  localSupabaseAnonKey: raw.localSupabaseAnonKey || "",
});

const getAvatarColor = (name = "") => {
  const colors = [
    "#1565C0", "#2E7D32", "#C62828", "#6A1B9A",
    "#00838F", "#E65100", "#4527A0", "#283593",
  ];
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash += name.charCodeAt(i);
  return colors[hash % colors.length];
};

const getStatusColor = (status) =>
  status === "Active" ? "success" : "error";

const matchesSearch = (company, term) =>
  ["name", "companyId", "contactPerson", "email", "city", "gstin", "state"]
    .some((key) => company[key]?.toLowerCase().includes(term));

const exportToCSV = (companies) => {
  const headers = [
    "Company ID", "Name", "Contact Person", "Email",
    "Phone", "Website", "GSTIN", "City", "State", "Status",
  ];
  const rows = companies.map((c) => [
    c.companyId      || "",
    c.name           || "",
    c.contactPerson  || "",
    c.email          || "",
    c.phoneNumber    || "",
    c.website        || "",
    c.gstin          || "",
    c.city           || "",
    c.state          || "",
    c.status         || "",
  ]);

  const csv = [headers, ...rows]
    .map((row) =>
      row
        .map((v) =>
          String(v).includes(",") || String(v).includes('"')
            ? `"${String(v).replace(/"/g, '""')}"`
            : v,
        )
        .join(","),
    )
    .join("\n");

  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.setAttribute(
    "download",
    `Companies_Export_${new Date().toISOString().split("T")[0]}.csv`,
  );
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

/* ═══════════════════════════════════════════════════════════════════════════
   SKELETON
═══════════════════════════════════════════════════════════════════════════ */

const CompanyTableSkeleton = () => (
  <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
      <Box>
        <Skeleton variant="text"    width={260} height={40} />
        <Skeleton variant="text"    width={380} height={20} />
      </Box>
      <Box sx={{ display: "flex", gap: 1.5 }}>
        <Skeleton variant="rounded" width={140} height={40} />
        <Skeleton variant="rounded" width={140} height={40} />
      </Box>
    </Box>
    <Box sx={{ mb: 2 }}>
      <Skeleton variant="rounded" width="40%" height={40} />
    </Box>
    <Box sx={{
      background: "#fff", borderRadius: "12px",
      padding: "20px", boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
    }}>
      <Box sx={{
        display: "flex", gap: 2, py: 1.5, mb: 1,
        borderBottom: "2px solid #e2e8f0",
      }}>
        {[50, 120, 160, 180, 120, 140, 80, 80, 60].map((w, i) => (
          <Skeleton key={i} variant="text" width={w} height={16} />
        ))}
      </Box>
      {Array.from({ length: 8 }, (_, i) => (
        <Box key={i} sx={{
          display: "flex", gap: 2, py: 1.5, alignItems: "center",
          borderBottom: "1px solid #f1f5f9",
        }}>
          <Skeleton variant="text"     width={40}  height={14} />
          <Skeleton variant="circular" width={34}  height={34} />
          <Skeleton variant="text"     width={140} height={14} />
          <Skeleton variant="text"     width={160} height={14} />
          <Skeleton variant="text"     width={120} height={14} />
          <Skeleton variant="text"     width={110} height={14} />
          <Skeleton variant="text"     width={100} height={14} />
          <Skeleton variant="rounded"  width={70}  height={24} />
          <Skeleton variant="text"     width={50}  height={14} />
        </Box>
      ))}
    </Box>
  </Container>
);

/* ═══════════════════════════════════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════════════════════════════════ */

const CompanyManagement = () => {
  const { currentUser } = useAuth();

  /* ── Modal state ────────────────────────────────────────────────────────── */
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal,   setShowEditModal]   = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  /* ── Data state ─────────────────────────────────────────────────────────── */
  const [companies,         setCompanies]         = useState([]);
  const [filteredCompanies, setFilteredCompanies] = useState([]);
  const [selectedCompany,   setSelectedCompany]   = useState(null);
  const [companyToDelete,   setCompanyToDelete]   = useState(null);
  const [loading,           setLoading]           = useState(true);
  const [syncingId,         setSyncingId]         = useState(null);
  const [permissionError,   setPermissionError]   = useState(null);
  const [notification,      setNotification]      = useState(null);

  /* ── Filter state ───────────────────────────────────────────────────────── */
  const [searchTerm, setSearchTerm] = useState("");

  /* ── Table / menu state ─────────────────────────────────────────────────── */
  const [anchorEl,          setAnchorEl]          = useState(null);
  const [menuTargetCompany, setMenuTargetCompany] = useState(null);
  const [page,              setPage]              = useState(0);
  const [rowsPerPage,       setRowsPerPage]       = useState(10);

  /* ── Notification helper ─────────────────────────────────────────────────── */
  const showNotification = useCallback((message, type = "success") => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 4000);
  }, []);

  /* ── Real-time subscription ──────────────────────────────────────────────── */
  useEffect(() => {
    if (!currentUser) {
      setLoading(false);
      setPermissionError("Please log in to access company data.");
      return;
    }

    if (currentUser.role !== "superadmin") {
      setPermissionError("Only Super Admins can access this page.");
      setLoading(false);
      return;
    }

    setLoading(true);

    const result = companyService.subscribeToCompanies((rawList) => {
      const normalised = rawList.map(normaliseCompany);
      setCompanies(normalised);
      setFilteredCompanies(normalised);
      setLoading(false);
    });

    if (result instanceof Promise) {
      let cleanup;
      result
        .then((fn) => { cleanup = fn; })
        .catch((err) => {
          console.error("Subscription error:", err);
          showNotification(err.message, "error");
          setLoading(false);
        });
      return () => { if (typeof cleanup === "function") cleanup(); };
    }

    return () => { if (typeof result === "function") result(); };
  }, [currentUser, showNotification]);

  /* ── Debounced search filter ─────────────────────────────────────────────── */
  const debouncedFilter = useMemo(
    () =>
      debounce((term, list) => {
        if (!term.trim()) {
          setFilteredCompanies(list);
          setPage(0);
          return;
        }
        const lower = term.toLowerCase();
        setFilteredCompanies(list.filter((c) => matchesSearch(c, lower)));
        setPage(0);
      }, 300),
    [],
  );

  useEffect(() => {
    debouncedFilter(searchTerm, companies);
    return () => debouncedFilter.cancel();
  }, [searchTerm, companies, debouncedFilter]);

 
  const handleModalSuccess = useCallback(
    async (savedCompany) => {
      const wasEdit = Boolean(selectedCompany);
      setShowCreateModal(false);
      setShowEditModal(false);
      setSelectedCompany(null);
      showNotification(
        wasEdit
          ? "Company metadata updated."
          : "Company registered successfully.",
      );

      // Safety net: refresh directly, don't rely only on the subscription
      try {
        const fresh = await companyService.getAllCompanies(currentUser);
        const normalised = fresh.map(normaliseCompany);
        setCompanies(normalised);
        setFilteredCompanies(normalised);
      } catch (e) {
        console.warn("[CompanyManagement] post-save refresh failed", e);
      }
    },
    [selectedCompany, showNotification, currentUser],
  );

  /** Status toggle — calls service → repo */
  const handleStatusToggle = async (company) => {
    try {
      const newStatus = await companyService.toggleCompanyStatus(
        company,
        currentUser,
      );
      await companyService.logCompanyActivity(
        company.id,
        "status_changed",
        { oldStatus: company.status, newStatus },
        currentUser,
      );
      showNotification(`Status switched to ${newStatus}.`);
    } catch (err) {
      showNotification(err.message, "error");
    }
  };

  /** Metric sync — calls service → repo */
  const handleSyncCounts = async (e, companyId) => {
    if (e) e.stopPropagation();
    setSyncingId(companyId);
    try {
      await companyService.syncCompanyCounts(companyId, currentUser);
      showNotification("Company metrics synchronised.");
    } catch (err) {
      showNotification("Metrics sync failed.", "error");
    } finally {
      setSyncingId(null);
    }
  };

  /* ── Export CSV ─────────────────────────────────────────────────────────── */
  const handleExportCSV = useCallback(() => {
    if (filteredCompanies.length === 0) {
      showNotification("No companies to export.", "warning");
      return;
    }
    exportToCSV(filteredCompanies);
    showNotification("Companies exported successfully.");
  }, [filteredCompanies, showNotification]);

  /* ── Menu helpers ───────────────────────────────────────────────────────── */
  const handleMenuOpen  = (e, company) => {
    setAnchorEl(e.currentTarget);
    setMenuTargetCompany(company);
  };
  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuTargetCompany(null);
  };

  const handleChangePage        = (_, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  /* ── Close handlers for modals ──────────────────────────────────────────── */
  const handleCloseCreateModal = useCallback(() => {
    setShowCreateModal(false);
  }, []);

  const handleCloseEditModal = useCallback(() => {
    setShowEditModal(false);
    setSelectedCompany(null);
  }, []);

  const handleCloseDeleteModal = useCallback(() => {
    setShowDeleteModal(false);
    setCompanyToDelete(null);
  }, []);

  /* ─────────────────────────────────────────────────────────────────────────
     GUARDS
  ───────────────────────────────────────────────────────────────────────── */

  if (permissionError) {
    return (
      <Box sx={{ p: 4 }}>
        <PermissionDenied message={permissionError} />
      </Box>
    );
  }

  if (loading) {
    return <CompanyTableSkeleton />;
  }

  /* ─────────────────────────────────────────────────────────────────────────
     RENDER
  ───────────────────────────────────────────────────────────────────────── */

  return (
    <Container
      maxWidth="xl"
      sx={{ mt: 4, mb: 4, display: "flex", flexDirection: "column", gap: 3 }}
    >
      {/* ── HEADER ──────────────────────────────────────────────────────── */}
      <Box sx={{
        display: "flex", justifyContent: "space-between",
        alignItems: "center", flexWrap: "wrap", gap: 2,
      }}>
        <Box>
          <Typography
            variant="h4" component="h1"
            sx={{ fontWeight: 700, color: "text.primary", letterSpacing: "-0.5px" }}
          >
            Company Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Register, manage, and monitor all corporate entities on the platform
          </Typography>
        </Box>

        <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
          <Button
            variant="contained" color="primary" startIcon={<AddIcon />}
            onClick={() => setShowCreateModal(true)} disableElevation
            sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600, px: 2.5 }}
          >
            Register Company
          </Button>
          <Button
            variant="outlined" color="secondary" startIcon={<FileDownloadIcon />}
            onClick={handleExportCSV}
            sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600 }}
          >
            Export CSV
          </Button>
        </Box>
      </Box>

      {/* ── SEARCH BAR ──────────────────────────────────────────────────── */}
      <Paper
        sx={{ p: 2.5, borderRadius: 3, bgcolor: "background.paper" }}
        variant="outlined"
      >
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={5}>
            <TextField
              fullWidth size="small" variant="outlined"
              label="Search Companies"
              placeholder="Search by name, ID, contact, email, city, GSTIN..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: "action.active" }} fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchTerm("")}>
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
            />
          </Grid>

          {searchTerm && (
            <Grid item xs={12} md={7}>
              <Typography variant="body2" color="text.secondary">
                Found <strong>{filteredCompanies.length}</strong>{" "}
                {filteredCompanies.length !== 1 ? "companies" : "company"}{" "}
                matching <em>"{searchTerm}"</em>
              </Typography>
            </Grid>
          )}
        </Grid>
      </Paper>

      {/* ── DATA TABLE ──────────────────────────────────────────────────── */}
      <TableContainer
        component={Paper}
        variant="outlined"
        sx={{
          borderRadius: 3,
          boxShadow: "0px 4px 16px rgba(0,0,0,0.02)",
          overflowX: "auto",
          "&::-webkit-scrollbar": { height: 8 },
          "&::-webkit-scrollbar-thumb": {
            bgcolor: "rgba(0,0,0,0.12)", borderRadius: 4,
          },
        }}
      >
        {filteredCompanies.length === 0 ? (
          <Box sx={{ p: 8, textAlign: "center" }}>
            <Alert
              severity="info"
              sx={{ justifyContent: "center", borderRadius: 2.5 }}
            >
              No companies found. Register one to get started.
            </Alert>
          </Box>
        ) : (
          <Table sx={{ minWidth: 1100 }} size="medium">
            <TableHead>
              <TableRow>
                {[
                  { label: "Sl.No",    align: "center" },
                  { label: "Company",  align: "left"   },
                  { label: "Contact",  align: "left"   },
                  { label: "GSTIN",    align: "left"   },
                  { label: "Location", align: "left"   },
                  { label: "Assets",   align: "center" },
                  { label: "Users",    align: "center" },
                  { label: "Status",   align: "center" },
                  { label: "Actions",  align: "center" },
                ].map(({ label, align }) => (
                  <TableCell
                    key={label} align={align}
                    sx={{
                      fontWeight: 700,
                      bgcolor: "background.neutral",
                      color: "text.primary",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {filteredCompanies
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((c, index) => (
                  <TableRow
                    key={c.id}
                    hover
                    sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                  >
                    <TableCell align="center">
                      <Typography
                        variant="body2" fontWeight={600} color="text.secondary"
                      >
                        {page * rowsPerPage + index + 1}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        <Avatar
                          sx={{
                            width: 36, height: 36,
                            fontSize: "0.9rem", fontWeight: 700,
                            bgcolor: getAvatarColor(c.name),
                          }}
                        >
                          {(c.name || "?")[0].toUpperCase()}
                        </Avatar>
                        <Box>
                          <Typography
                            variant="body2" fontWeight={600} color="text.primary"
                          >
                            {c.name}
                          </Typography>
                          <Typography
                            variant="caption" color="text.disabled"
                            sx={{ fontFamily: "monospace" }}
                          >
                            {c.companyId}
                          </Typography>
                          {c.website && c.website !== "—" && (
                            <Box
                              sx={{
                                display: "flex", alignItems: "center",
                                gap: 0.4, mt: 0.2,
                              }}
                            >
                              <LanguageIcon sx={{ fontSize: 11, color: "text.disabled" }} />
                              <Typography variant="caption" color="text.disabled">
                                {c.website}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </Box>
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2" fontWeight={500} color="text.primary">
                        {c.contactPerson}
                      </Typography>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.3 }}
                      >
                        <EmailIcon sx={{ fontSize: 11, color: "text.disabled" }} />
                        <Typography
                          variant="caption" color="text.secondary"
                          sx={{ fontFamily: "monospace", fontSize: "0.75rem" }}
                        >
                          {c.email}
                        </Typography>
                      </Box>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.2 }}
                      >
                        <PhoneIcon sx={{ fontSize: 11, color: "text.disabled" }} />
                        <Typography variant="caption" color="text.secondary">
                          {c.phoneNumber}
                        </Typography>
                      </Box>
                    </TableCell>

                    <TableCell>
                      <Typography
                        variant="body2" color="text.secondary"
                        sx={{ fontFamily: "monospace", fontSize: "0.8rem" }}
                      >
                        {c.gstin}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.5 }}>
                        <LocationOnIcon
                          sx={{ fontSize: 13, color: "text.disabled", mt: 0.2 }}
                        />
                        <Box>
                          <Typography variant="body2" color="text.primary">
                            {c.city ? `${c.city}, ` : ""}{c.state}
                          </Typography>
                          <Typography variant="caption" color="text.disabled">
                            {c.country}{c.pincode && c.pincode !== "—" ? ` — ${c.pincode}` : ""}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>

                    <TableCell align="center">
                      <Tooltip title="Sync asset & user counts" arrow>
                        <Chip
                          label={c.assets}
                          size="small"
                          onClick={(e) => handleSyncCounts(e, c.id)}
                          icon={
                            syncingId === c.id
                              ? <RefreshIcon sx={{ fontSize: "14px !important", animation: "spin 1s linear infinite" }} />
                              : undefined
                          }
                          sx={{
                            fontWeight: 600, fontSize: "0.75rem",
                            cursor: "pointer",
                            "@keyframes spin": {
                              "0%":   { transform: "rotate(0deg)"   },
                              "100%": { transform: "rotate(360deg)" },
                            },
                          }}
                        />
                      </Tooltip>
                    </TableCell>

                    <TableCell align="center">
                      <Chip
                        label={c.userCount}
                        size="small"
                        color="info"
                        variant="outlined"
                        sx={{ fontWeight: 600, fontSize: "0.75rem" }}
                      />
                    </TableCell>

                    <TableCell align="center">
                      <Chip
                        label={c.status}
                        size="small"
                        color={getStatusColor(c.status)}
                        onClick={() => handleStatusToggle(c)}
                        icon={
                          c.status === "Active"
                            ? <ToggleOnIcon />
                            : <ToggleOffIcon />
                        }
                        sx={{ fontWeight: 600, fontSize: "0.72rem", cursor: "pointer" }}
                      />
                    </TableCell>

                    <TableCell align="center">
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuOpen(e, c)}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        )}
      </TableContainer>

      {/* ── PAGINATION ──────────────────────────────────────────────────── */}
      {filteredCompanies.length > 0 && (
        <Box sx={{
          display: "flex", justifyContent: "space-between",
          alignItems: "center", py: 1.5,
          borderTop: "1px solid", borderColor: "divider",
        }}>
          <Typography variant="caption" color="text.secondary" fontWeight={500}>
            Showing {page * rowsPerPage + 1} to{" "}
            {Math.min((page + 1) * rowsPerPage, filteredCompanies.length)} of{" "}
            {filteredCompanies.length} companies
          </Typography>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={filteredCompanies.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{ border: "none", overflow: "hidden" }}
          />
        </Box>
      )}

      {/* ── CONTEXT MENU ────────────────────────────────────────────────── */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        TransitionComponent={Fade}
        PaperProps={{
          sx: {
            minWidth: 200, borderRadius: 2.5,
            boxShadow: "0px 4px 20px rgba(0,0,0,0.08)",
          },
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top",    horizontal: "right" }}
      >
        <MenuItem
          onClick={() => {
            setSelectedCompany(menuTargetCompany);
            setShowEditModal(true);
            handleMenuClose();
          }}
          sx={{ gap: 1.5, py: 1 }}
        >
          <EditIcon fontSize="small" color="action" />
          <Typography variant="body2" fontWeight={500}>Edit Company</Typography>
        </MenuItem>

        <MenuItem
          onClick={(e) => {
            handleSyncCounts(e, menuTargetCompany?.id);
            handleMenuClose();
          }}
          disabled={syncingId === menuTargetCompany?.id}
          sx={{ gap: 1.5, py: 1 }}
        >
          <RefreshIcon fontSize="small" color="action" />
          <Typography variant="body2" fontWeight={500}>Sync Counters</Typography>
        </MenuItem>

        <Divider sx={{ my: 0.5 }} />

        <MenuItem
          onClick={() => {
            setCompanyToDelete(menuTargetCompany);
            setShowDeleteModal(true);
            handleMenuClose();
          }}
          sx={{ gap: 1.5, py: 1, color: "error.main" }}
        >
          <DeleteIcon fontSize="small" color="error" />
          <Typography variant="body2" fontWeight={600} color="error.main">
            Delete Company
          </Typography>
        </MenuItem>
      </Menu>

      {/* ── TOAST ───────────────────────────────────────────────────────── */}
      {notification && (
        <ToastNotification
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification(null)}
        />
      )}

  

      {/* CREATE MODAL */}
      <CreateEditCompanyModal
        key="create-company-modal"     
        open={showCreateModal}
        company={null}
        onClose={handleCloseCreateModal}
        onSuccess={handleModalSuccess}
        existingCompanies={companies}
      />

      {/* EDIT MODAL */}
      <CreateEditCompanyModal
        key={`edit-company-modal-${selectedCompany?.id || 'none'}`}  
        open={showEditModal && Boolean(selectedCompany)}
        company={selectedCompany}
        onClose={handleCloseEditModal}
        onSuccess={handleModalSuccess}
        existingCompanies={companies}
      />

      {/* DELETE MODAL */}
      {showDeleteModal && companyToDelete && (
        <CompanyDeleteModal
          isOpen={showDeleteModal}
          onClose={handleCloseDeleteModal}
          company={companyToDelete}
          currentUser={currentUser}
          showNotification={showNotification}
          logCompanyActivity={(id, action, details) =>
            companyService.logCompanyActivity(id, action, details, currentUser)
          }
        />
      )}
    </Container>
  );
};

export default CompanyManagement;